// Web adapters for the portal-core ports.
//
// The whole platform boundary for this app lives in this file. The React Native
// app will have its own version — Firebase native SDK, expo-secure-store — and
// nothing else has to change.

import type { AuthPort, ConfigPort, PortalIdentity, StoragePort } from '@invoiceflow/portal-core';
import {
  firebaseSignOut,
  getIdToken,
  signInWithApple,
  signInWithGoogle,
  signInWithPortalToken,
  watchIdToken,
} from './firebase';

const toIdentity = (user: {
  uid: string;
  email: string | null;
  displayName: string | null;
}): PortalIdentity => ({
  uid: user.uid,
  email: user.email,
  displayName: user.displayName,
});

export const webAuthPort: AuthPort = {
  getIdToken,
  signInWithCustomToken: async token => toIdentity(await signInWithPortalToken(token)),
  signInWithGoogle: async () => toIdentity(await signInWithGoogle()),
  signInWithApple: async () => toIdentity(await signInWithApple()),
  onIdTokenChanged: callback => watchIdToken(user => callback(user ? toIdentity(user) : null)),
  signOut: firebaseSignOut,
};

/**
 * localStorage is synchronous; the port is not, because the native adapter
 * cannot be. Wrapping it costs nothing and keeps the core unaware of which
 * platform it is running on.
 */
export const webStoragePort: StoragePort = {
  get: async key => {
    try {
      return localStorage.getItem(key);
    } catch {
      // Private browsing, or storage disabled.
      return null;
    }
  },
  set: async (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* not worth failing a flow over */
    }
  },
  remove: async key => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export const webConfigPort: ConfigPort = {
  apiBaseUrl: import.meta.env.VITE_API_URL || '/api/v1',
  portalUrl: window.location.origin,
};
